import React from "react";
import PropTypes from "prop-types";
import { track as rawTrack } from "../../universal/track";

/**
 * Context that holds the active template codename so every track() call
 * within the public shell automatically gets { template } injected.
 */
const TemplateTrackingContext = React.createContext({
  codename: "original",
  isResolved: true,
});

export function TemplateTrackingProvider({ codename, isResolved = true, children }) {
  return (
    <TemplateTrackingContext.Provider
      value={{
        codename: codename || "original",
        isResolved,
      }}
    >
      {children}
    </TemplateTrackingContext.Provider>
  );
}

TemplateTrackingProvider.propTypes = {
  codename: PropTypes.string,
  isResolved: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * Returns a track() wrapper that automatically appends { template: codename }
 * to every event payload.
 *
 * Usage inside a template component:
 *   const track = useTemplateTrack();
 *   track("homepage_view");  // sends { event, template: "minimal", visitorId, ... }
 */
export function useTemplateTrack() {
  const { codename } = React.useContext(TemplateTrackingContext);
  return React.useCallback(
    (event, payload = {}) => {
      rawTrack(event, { ...payload, template: codename });
    },
    [codename]
  );
}

export function useTemplateName() {
  return React.useContext(TemplateTrackingContext).codename;
}

export function useTemplateResolved() {
  return React.useContext(TemplateTrackingContext).isResolved;
}
